import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { agencyApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Badge from '../../components/Badge.jsx';
import Modal from '../../components/Modal.jsx';
import { Field, Select, TextInput } from '../../components/Field.jsx';

const TIMEZONES = ['Europe/Paris', 'Asia/Jerusalem', 'Europe/London', 'America/New_York', 'UTC'];

const EMPTY = {
  name: '',
  code: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  timezone: 'Europe/Paris',
  status: 'ACTIVE',
};

/**
 * Agencies are the tenant boundary: each one holds its own telepros, its own
 * sales people and its own customers. Only an administrator creates them; an
 * agency manager sees the single row that is theirs.
 */
const AgenciesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();

  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, refresh } = useApi(() => agencyApi.list({ q: search }), [search]);
  const isAdmin = user.role === 'ADMIN';

  const submit = async () => {
    setBusy(true);
    try {
      if (form.id) {
        const { id, counts, staff, createdAt, updatedAt, ...patch } = form;
        await agencyApi.update(id, patch);
      } else {
        await agencyApi.create(form);
      }
      toast.success(t('common.save'));
      setForm(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (agency) => {
    try {
      await agencyApi.disable(agency.id);
      toast.success(t('agencies.disabled'));
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('agencies.name'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.name}</span>
          <span className="cell-muted">
            <code>{row.code}</code> {[row.city, row.country].filter(Boolean).join(', ')}
          </span>
        </div>
      ),
    },
    {
      key: 'staff',
      header: t('agencies.staff'),
      render: (row) => (
        <div className="chip-list chip-list--tight">
          <span className="chip">{row.counts.telepros} {t('roles.TELEPRO')}</span>
          <span className="chip">{row.counts.commercials} {t('roles.COMMERCIAL')}</span>
          <span className="chip">{row.counts.managers} {t('roles.AGENCY')}</span>
        </div>
      ),
    },
    { key: 'customers', header: t('dashboard.customers'), className: 'cell-right', render: (row) => row.counts.customers },
    { key: 'timezone', header: t('settings.timezone') },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <Badge value={row.status} label={row.status === 'ACTIVE' ? t('users.statusActive') : t('users.statusDisabled')} />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (row) => (
        <div className="row-actions">
          <button type="button" className="btn btn--small" onClick={() => setForm({ ...row })}>
            {t('common.edit')}
          </button>
          {isAdmin && row.status === 'ACTIVE' ? (
            <button type="button" className="btn btn--small btn--ghost" onClick={() => disable(row)}>
              {t('users.disable')}
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('agencies.title')}
        subtitle={t('agencies.subtitle')}
        actions={
          isAdmin ? (
            <button type="button" className="btn btn--primary" onClick={() => setForm({ ...EMPTY })}>
              {t('agencies.newAgency')}
            </button>
          ) : null
        }
      />

      <Toolbar>
        <TextInput value={search} onChange={setSearch} placeholder={t('common.search')} />
        <span className="toolbar__count">
          {data?.total ?? 0} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable columns={columns} rows={data?.items} loading={loading} emptyTitle={t('common.noData')} />

      <Modal
        open={Boolean(form)}
        title={form?.id ? t('common.edit') : t('agencies.newAgency')}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {form ? (
          <div className="form-grid">
            <Field label={t('agencies.name')} htmlFor="a-name" required>
              <TextInput id="a-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            </Field>
            <Field label={t('agencies.code')} htmlFor="a-code" required hint={t('agencies.codeHint')}>
              <TextInput
                id="a-code"
                value={form.code}
                onChange={(v) => setForm({ ...form, code: v.toUpperCase() })}
                disabled={Boolean(form.id) && !isAdmin}
              />
            </Field>
            <Field label={t('customers.city')} htmlFor="a-city">
              <TextInput id="a-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            </Field>
            <Field label={t('customers.country')} htmlFor="a-country">
              <TextInput id="a-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            </Field>
            <Field label={t('customers.phone')} htmlFor="a-phone">
              <TextInput id="a-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </Field>
            <Field label={t('customers.email')} htmlFor="a-email">
              <TextInput id="a-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </Field>
            <Field label={t('settings.timezone')} htmlFor="a-tz">
              <Select
                id="a-tz"
                value={form.timezone}
                onChange={(v) => setForm({ ...form, timezone: v })}
                options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
              />
            </Field>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default AgenciesPage;
